import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Badge, Button, Card, CardBody, Col, Input, Label, Row } from 'reactstrap';
import Breadcrumb from '../../components/common/breadcrumb';
import { PodDoc } from '../sase-sorgulama/components/DevirTeslim';
import { getMasterData, getReturnCases, refuseDelivery, updateReturnCase } from './api';
import './devir-teslim.css';

// Refused / returned cars: the driver's refusal at the dealer, the return scan at the compound (reception area), and the
// control tower's investigation and decision. Re-ship and blockage orders themselves go through Veri Yükleme >
// Blokaj/Sevk Yeri Değişikliği (SEVK / BLOKAJ) and Blokaj Ekle; the list shows when the new order or blockage exists.
const RETURN_STATUS = { YOLDA: ['Depoya dönüyor', 'info'], DEPODA: ['Depoda – karar bekliyor', 'warning'], KARAR: ['Karar verildi', 'success'] };
const DECISIONS = [['YENIDEN_SEVK', 'Yeniden sevk (yeni sevk emri)'], ['STOK', 'Stoğa al (sonra sevk edilecek)'], ['BLOKAJ', 'Blokaj (tipiyle)']];
const SOURCE = { SURUCU: 'şoför (el terminali)', DEPO: 'depo girişi', OPERATOR: 'operatör', TASIYICI: 'taşıyıcı portalı' };

// operator records a failed delivery reported by phone / e-mail (no signatures): the case starts as "on the way back",
// or "at the compound" when the car is already in a depot
const NewReturnCase = ({ reasons, onSaved, onCancel }) => {
    const [f, setF] = useState({ chassis_no: '', reason_code: '', note: '' });
    const save = async () => {
        try {
            const res = await refuseDelivery({ ...f, chassis_no: f.chassis_no.trim().toUpperCase(), source: 'OPERATOR' });
            toast.success(`İade kaydı açıldı: ${res.case_no}`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kaydedilemedi');
        }
    };
    return (
        <div className="dt-draft mb-3">
            <Row>
                <Col md="3"><Label>Şase</Label>
                    <Input value={f.chassis_no} maxLength={17} onChange={(e) => setF({ ...f, chassis_no: e.target.value.toUpperCase() })} /></Col>
                <Col md="3"><Label>Neden</Label>
                    <Input type="select" value={f.reason_code} onChange={(e) => setF({ ...f, reason_code: e.target.value })}>
                        <option value="">— seçiniz —</option>
                        {reasons.map((r) => <option key={r.code} value={r.code}>{r.name_tr}</option>)}
                    </Input></Col>
                <Col md="4"><Label>Açıklama (kim bildirdi, ne oldu)</Label>
                    <Input value={f.note} placeholder="ör. Şoför telefonla bildirdi: bayi kapalı" onChange={(e) => setF({ ...f, note: e.target.value })} /></Col>
                <Col md="2" className="d-flex align-items-end">
                    <Button color="primary" className="mr-1" disabled={f.chassis_no.trim().length !== 17 || !f.reason_code} onClick={save}>Aç</Button>
                    <Button color="secondary" onClick={onCancel}>Vazgeç</Button></Col>
            </Row>
        </div>
    );
};

const ReturnEditor = ({ c, reasons, onSaved, onCancel }) => {
    const [reason, setReason] = useState(c.reason_code || '');
    const [note, setNote] = useState('');
    const [decision, setDecision] = useState('');
    const save = async () => {
        try {
            await updateReturnCase({ id: c.id, reason_code: reason !== (c.reason_code || '') ? reason : '', note, decision });
            toast.success('İade kaydı güncellendi');
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Kaydedilemedi');
        }
    };
    return (
        <div className="dt-draft mt-2">
            <Row>
                <Col md="3"><Label>Neden</Label>
                    <Input type="select" value={reason} onChange={(e) => setReason(e.target.value)}>
                        <option value="">— araştırılacak —</option>
                        {reasons.map((r) => <option key={r.code} value={r.code}>{r.name_tr}</option>)}
                    </Input></Col>
                <Col md="4"><Label>Açıklama / inceleme notu</Label>
                    <Input value={note} placeholder="ör. Bayi ile görüşüldü, araç GÜLAN'a ait" onChange={(e) => setNote(e.target.value)} /></Col>
                <Col md="3"><Label>Karar</Label>
                    <Input type="select" value={decision} onChange={(e) => setDecision(e.target.value)}>
                        <option value="">— şimdilik yok (sadece neden / not) —</option>
                        {DECISIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </Input></Col>
                <Col md="2" className="d-flex align-items-end">
                    <Button color="primary" className="mr-1" onClick={save}>Kaydet</Button>
                    <Button color="secondary" onClick={onCancel}>Vazgeç</Button></Col>
            </Row>
            {reason === 'BAYI_YER_YOK' && (
                <div className="text-warning mt-1">Bayide yer yok: aracın yeniden planlanmaması için bayi talebi blokajı önerilir (Veri Yükleme &gt;
                    Blokaj/Sevk Yeri Değişikliği, BLOKAJ, talep eden bayi). Bayi yer açınca SEVK ile kaldırılır.</div>
            )}
            <div className="dt-muted mt-1">
                Yeniden sevk: yeni sevk emri Veri Yükleme &gt; Blokaj/Sevk Yeri Değişikliği (SEVK) ile verilir; araç emirle grupaj hazırlamada görünür.
                Blokaj: Blokaj Ekle ile. Karar kaydı iade kaydını kapatır.</div>
        </div>
    );
};

const ReturnCases = ({ reasons }) => {
    const [onlyOpen, setOnlyOpen] = useState(true);
    const [vin, setVin] = useState('');
    const [list, setList] = useState(null);
    const [editing, setEditing] = useState(null);
    const [adding, setAdding] = useState(false);
    const load = useCallback(() => {
        // a VIN search finds the car's cases whether open or closed
        const v = vin.trim().length >= 6 ? vin.trim() : '';
        getReturnCases(onlyOpen, v).then(setList).catch((e) => { setList([]); toast.error(e?.response?.data?.error || 'İade kayıtları alınamadı'); });
    }, [onlyOpen, vin]);
    useEffect(() => { load(); }, [load]);
    const rows = list || [];
    return (
        <Card><CardBody>
            <div className="d-flex align-items-center mb-2" style={{ gap: 12 }}>
                <Input bsSize="sm" style={{ maxWidth: 260 }} placeholder="Şase ara (açık ve kapalı)" value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())} />
                <label className="mb-0"><input type="checkbox" checked={onlyOpen} disabled={vin.trim().length >= 6}
                    onChange={(e) => setOnlyOpen(e.target.checked)} /> Yalnızca açık kayıtlar</label>
                <span className="dt-muted">{rows.filter((c) => c.status === 'YOLDA').length} depoya dönüyor ·{' '}
                    {rows.filter((c) => c.status === 'DEPODA').length} depoda karar bekliyor</span>
                <Button size="sm" color="primary" outline className="ml-auto" onClick={() => setAdding(!adding)}>+ Yeni iade kaydı</Button>
            </div>
            {adding && <NewReturnCase reasons={reasons} onSaved={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />}
            <div className="dt-muted mb-2">Teslim edilemeyen araç şoför tarafından el terminalinde (neden, imza, fotoğraf) veya depo girişinde (bayi iadesi okutması) kaydedilir;
                araç resepsiyon alanına alınır. Ertesi sabah aracı inceleyip nedeni güncelleyin ve kararı kaydedin.</div>
            <table className="ct-table">
                <thead><tr><th>İade</th><th>VIN</th><th>Neden</th><th>Emirdeki varış → teslim denenen</th><th>Taşıyıcı</th><th>Durum / konum</th><th>Yeni emir / blokaj</th><th></th></tr></thead>
                <tbody>{rows.map((c) => (
                    <React.Fragment key={c.id}>
                        <tr>
                            <td><b>{c.case_no}</b><div className="dt-muted">{moment(c.opened_at).format('DD.MM.YYYY HH:mm')} · {SOURCE[c.source]}</div></td>
                            <td><b>{c.chassis_no}</b><div className="dt-muted">{c.brand} {c.model}</div></td>
                            <td>{c.reason_name ? <Badge color="warning">{c.reason_name}</Badge> : <Badge color="secondary">araştırılacak</Badge>}
                                {c.note && <div className="dt-muted">{c.note}</div>}</td>
                            <td>{c.planned_location || '—'}{c.attempted_location && <> → <b>{c.attempted_location}</b></>}</td>
                            <td>{c.carrier || '—'}<div className="dt-muted">{c.groupage_no}</div></td>
                            <td><Badge color={RETURN_STATUS[c.status][1]}>{RETURN_STATUS[c.status][0]}</Badge>
                                <div className="dt-muted">{c.returned_at ? `${c.return_warehouse || ''} · ${moment(c.returned_at).format('DD.MM.YYYY HH:mm')}` : ''}</div>
                                {c.current_place && <div className="dt-muted">{c.current_place}</div>}
                                {c.status !== 'KARAR' && <div className={c.age_hours > 24 ? 'ci-age-late' : 'dt-muted'}>{c.age_hours.toFixed(1)} saat</div>}
                                {c.decision && <div><b>{DECISIONS.find(([v]) => v === c.decision)?.[1]}</b></div>}</td>
                            <td>{c.new_order ? <div><Badge color="success">yeni emir</Badge> {c.new_order}</div> : <span className="dt-muted">yeni emir yok</span>}
                                {c.blocked && <div><Badge color="danger">{c.blocked}</Badge></div>}</td>
                            <td>{c.status !== 'KARAR' && <Button size="sm" color="primary" outline onClick={() => setEditing(editing === c.id ? null : c.id)}>İncele / karar</Button>}</td>
                        </tr>
                        <tr><td colSpan="8" style={{ borderTop: 0 }}>
                            {c.refusal && (
                                <div className="dt-muted">Bayide ret: {moment(c.refusal.refused_at).format('DD.MM.YYYY HH:mm')} · {c.refusal.location} · şoför {c.refusal.driver_person}
                                    {c.refusal.receiver && ` · ${c.refusal.receiver}${c.refusal.receiver_person ? ` (${c.refusal.receiver_person})` : ''}`}
                                    {c.refusal.receiver_signature ? ' · bayi imzaladı' : ' · bayi imzalamadı'}
                                    <span className="ct-photos ml-2">{[c.refusal.driver_signature, ...c.refusal.photos].map((p) => <PodDoc key={p} path={p} />)}</span></div>
                            )}
                            {c.events.map((e, i) => (
                                <div key={i} className="dt-muted">{moment(e.at).format('DD.MM.YYYY HH:mm')} {e.by_name}: {{ RET: 'teslim edilemedi', IADE_GIRIS: 'depo girişi (resepsiyon)', NEDEN: 'neden', KARAR: 'karar', NOT: 'not' }[e.kind]}
                                    {e.reason_name ? ` – ${e.reason_name}` : ''}{e.decision ? ` – ${DECISIONS.find(([v]) => v === e.decision)?.[1]}` : ''}{e.note ? ` (${e.note})` : ''}</div>
                            ))}
                            {editing === c.id && <ReturnEditor c={c} reasons={reasons} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
                        </td></tr>
                    </React.Fragment>
                ))}</tbody>
            </table>
            {list && rows.length === 0 && <div className="dt-muted p-2">İade kaydı yok.</div>}
        </CardBody></Card>
    );
};

// Operasyonlar > Operasyon İzleme > İadeler / Teslim Edilemeyen (was a tab of Devir-Teslim ve Hasar İzleme)
const Iadeler = () => {
    const [reasons, setReasons] = useState([]);
    useEffect(() => {
        getMasterData().then((md) => setReasons((md.refusal_reasons || []).filter((r) => r.is_active))).catch(() => {});
    }, []);
    return (
        <div>
            <Breadcrumb parent="Operasyon İzleme" current="İadeler / Teslim Edilemeyen" />
            <ReturnCases reasons={reasons} />
        </div>
    );
};

export default Iadeler;
